import { z } from "zod";
import { compareText, fail, ok, type FieldKind } from "../kind";

/**
 * Un fichier déjà téléversé sur R2. Les références passent par la synchro
 * R2 des nodeDatas : supprimer la ligne libère ses fichiers.
 */
export type FileRef = {
  key: string;
  // URL publique renvoyée par l'upload : de quoi afficher sans requête.
  url: string;
  name: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
};

export const FILE_CATEGORIES = ["image", "pdf", "audio", "video", "other"] as const;
export type FileCategory = (typeof FILE_CATEGORIES)[number];

export const fileSettings = z.object({
  // Vide : tout est accepté.
  accept: z.array(z.enum(FILE_CATEGORIES)).default([]),
  multiple: z.boolean().default(true),
  maxSizeMb: z.number().positive().max(500).optional(),
  display: z.enum(["thumbnails", "list", "cover"]).default("thumbnails"),
});

const fileRefSchema = z.object({
  key: z.string().min(1).max(1024),
  url: z.string().url().max(2048),
  name: z.string().min(1).max(255),
  mime: z.string().min(1).max(255),
  size: z.number().int().nonnegative(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export function fileCategory(mime: string): FileCategory {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "other";
}

/**
 * Le refus qui tient aux réglages (type, taille), ou null. Partagé par
 * `coerce`, la vérification avant l'envoi (src/fields/shared/fileRefusal.ts)
 * et la recopie d'une conversion (lib/fieldExtraction.ts).
 */
export function fileRefusal(
  settings: { accept: readonly FileCategory[]; maxSizeMb?: number },
  file: Pick<FileRef, "name" | "mime" | "size">,
): string | null {
  if (settings.accept.length > 0 && !settings.accept.includes(fileCategory(file.mime))) {
    return `"${file.name}" is not an accepted file type.`;
  }
  if (settings.maxSizeMb !== undefined && file.size > settings.maxSizeMb * 1024 * 1024) {
    return `"${file.name}" is larger than ${settings.maxSizeMb} MB.`;
  }
  return null;
}

const MAX_FILES = 100;

export const fileKind: FieldKind<"file"> = {
  type: "file",
  settings: fileSettings,
  value: z.array(fileRefSchema).min(1).max(MAX_FILES),
  coerce(input, settings) {
    const items = Array.isArray(input) ? input : [input];
    const files: FileRef[] = [];
    for (const item of items) {
      if (typeof item === "string") {
        return fail("Files must be uploaded first; a URL or a name is not a file.");
      }
      const parsed = fileRefSchema.safeParse(item);
      if (!parsed.success) return fail("Invalid file reference.");
      const file = parsed.data;
      const refusal = fileRefusal(settings, file);
      if (refusal) return fail(refusal);
      if (!files.some((existing) => existing.key === file.key)) files.push(file);
    }
    if (files.length === 0) return ok(null);
    if (!settings.multiple && files.length > 1) return fail("Only one file is accepted.");
    if (files.length > MAX_FILES) return fail(`At most ${MAX_FILES} files.`);
    return ok(files);
  },
  isEmpty: (value) => value.length === 0,
  format: (value) => value.map((file) => file.name).join(", "),
  compare: (a, b) => compareText(a[0]?.name ?? "", b[0]?.name ?? "") || a.length - b.length,
  filters: [],
  aggregates: ["count", "count_empty", "count_not_empty", "percent_empty", "percent_not_empty"],
};
